import { useRef, useState, useEffect } from 'react';
import { CheckCircle2, Circle, Clock } from 'lucide-react';
import { useTr } from '../contexts/LanguageContext';

const useInView = (threshold = 0.05) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const statusConfig = {
  complete: { icon: CheckCircle2, dot: '#55d532', border: '#55d532', glow: 'rgba(85,213,50,0.45)', bg: 'rgba(85,213,50,0.12)' },
  active:   { icon: Clock,        dot: '#30d5c8', border: '#30d5c8', glow: 'rgba(48,213,200,0.5)',  bg: 'rgba(48,213,200,0.1)' },
  upcoming: { icon: Circle,       dot: 'rgba(255,255,255,0.18)', border: 'rgba(255,255,255,0.15)', glow: 'none', bg: 'transparent' },
};

const PhaseCard = ({ phase, index, parentInView }) => {
  const { ref, inView } = useInView(0.04);
  const cfg = statusConfig[phase.status];
  const Icon = cfg.icon;
  return (
    <div ref={ref} data-testid={`roadmap-${phase.id}`}
      className={`relative flex gap-4 sm:gap-6 anim-hidden ${(inView || parentInView) ? 'anim-visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}>
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-mono text-xs sm:text-sm font-black z-10"
          style={{ background: cfg.bg, border: `2px solid ${cfg.border}`, color: cfg.dot, boxShadow: phase.status !== 'upcoming' ? `0 0 20px ${cfg.glow}` : 'none' }}>
          {phase.number}
        </div>
        {index < 4 && (
          <div className="w-px flex-1 min-h-[24px] mt-1"
            style={{ background: phase.status === 'complete' ? 'linear-gradient(#55d532, rgba(48,213,200,0.3))' : phase.status === 'active' ? 'linear-gradient(#30d5c8, rgba(48,213,200,0.15))' : 'rgba(255,255,255,0.07)' }} />
        )}
      </div>
      <div className="glass card-3d rounded-2xl p-5 sm:p-6 mb-5 sm:mb-6 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
          <div>
            <p className="text-[10px] sm:text-xs font-mono tracking-widest uppercase mb-1" style={{ color: cfg.dot }}>{phase.phaseLabel}</p>
            <h3 className="font-outfit font-black text-white text-xl sm:text-2xl">{phase.title}</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-0.5">
            <span className="text-xs font-mono text-white/35 bg-white/5 px-3 py-1 rounded-full">{phase.subtitle}</span>
            <span className="flex items-center gap-1.5 text-xs font-mono px-3 py-1 rounded-full"
              style={{ color: cfg.dot, background: cfg.bg, border: `1px solid ${cfg.border}30` }}>
              <Icon size={10} />{phase.statusLabel}
            </span>
          </div>
        </div>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {phase.items.map(item => (
            <li key={item} className="flex items-start gap-2.5 text-xs sm:text-sm text-white/65">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: cfg.dot }} />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const RoadmapSection = () => {
  const t = useTr();
  const { ref, inView } = useInView();

  const phases = [
    {
      id: 'phase-1', number: '01', status: 'complete',
      phaseLabel: t('Phase 01', '1단계'),
      title: t('Foundation', '기반 구축'),
      subtitle: t('Q1 to Q2 2026', '2026년 1분기 - 2분기'),
      statusLabel: t('Complete', '완료'),
      items: t(
        [
          'SwarmCore and SwarmBadge deployed on opBNB Mainnet',
          'On-chain registration, daily check-ins, streak scoring, and referral tracking live',
          'Soulbound badges (Pioneer, Builder, OG) live',
          'core.swarmbase.io and core.swarmbase.io/points live',
          'Contracts source-verified on opBNBscan',
          'Independent audit completed',
          'Whitepaper published',
        ],
        [
          'opBNB 메인넷에 SwarmCore 및 SwarmBadge 배포',
          '온체인 등록, 일일 체크인, 스트릭 점수, 추천 추적 활성화',
          '소울바운드 배지(Pioneer, Builder, OG) 출시',
          'core.swarmbase.io 및 core.swarmbase.io/points 운영 개시',
          '컨트랙트 소스 코드 opBNBscan에서 공개 검증 완료',
          '독립적인 감사 완료',
          '기술 백서 발간',
        ]
      ),
    },
    {
      id: 'phase-2', number: '02', status: 'active',
      phaseLabel: t('Phase 02', '2단계'),
      title: t('Token Launch', '토큰 출시'),
      subtitle: t('Q3 2026', '2026년 3분기'),
      statusLabel: t('Active', '진행 중'),
      items: t(
        [
          '$SWARM deployed on BNB Smart Chain',
          'Full 1B supply distributed on-chain in a single transaction',
          'Allocation vesting configured on chain',
          'Multisig wallets configured for allocations',
          'DEX liquidity launched',
          'Community allocation distributed following the TGE snapshot and off-chain filtering',
        ],
        [
          'BNB 스마트 체인에 $SWARM 토큰 배포',
          '단일 트랜잭션으로 전체 10억 개 공급량 온체인 배분',
          '온체인 배분 베스팅 설정 완료',
          '잔여 배분을 위한 멀티시그 지갑 설정',
          'DEX 유동성 공급 개시',
          'TGE 스냅샷 및 오프체인 필터링 후 커뮤니티 에어드롭 배분',
        ]
      ),
    },
    {
      id: 'phase-3', number: '03', status: 'upcoming',
      phaseLabel: t('Phase 03', '3단계'),
      title: t('Protocol Activation', '프로토콜 활성화'),
      subtitle: t('Q4 2026', '2026년 4분기'),
      statusLabel: t('Upcoming', '예정'),
      items: t(
        [
          'StakeVault deployed on BSC: validator collateral, delegation, 14-day unbonding',
          'GovernanceModule deployed with two-tier on-chain governance',
          'RewardDistributor deployed for distribution of rewards for verified work',
          'Agent marketplace live with real compute tasks routable on-chain',
          'Developer API launched for programmatic agent deployment',
        ],
        [
          'BSC에 StakeVault 배포(검증인 담보, 위임, 14일 언본딩)',
          '2단계 온체인 거버넌스 GovernanceModule 배포',
          '검증된 작업에 대한 보상 배분을 위한 RewardDistributor 배포',
          '온체인 컴퓨팅 작업이 라우팅 가능한 에이전트 마켓플레이스 출시',
          '프로그래밍 방식의 에이전트 배포를 위한 개발자 API 출시',
        ]
      ),
    },
    {
      id: 'phase-4', number: '04', status: 'upcoming',
      phaseLabel: t('Phase 04', '4단계'),
      title: t('Swarm Infrastructure', '스웜 인프라'),
      subtitle: t('Q1 2027', '2027년 1분기'),
      statusLabel: t('Upcoming', '예정'),
      items: t(
        [
          'SwarmCoordinator deployed on opBNB with full swarm DAG management',
          'Declarative swarm topology, nested sub-swarms, fault-tolerant execution live',
          'Automatic agent reassignment when agents fail or produce low-quality outputs',
          'VerifierGateway with TEE attestation pipeline (Intel SGX and AMD SEV)',
          'Only TEE-attested outputs eligible for rewards',
          'Developer SDK released; grants program activated from the Ecosystem allocation',
        ],
        [
          'opBNB에 SwarmCoordinator 배포(완전한 스웜 DAG 관리)',
          '선언적 스웜 토폴로지, 중첩 서브스웜, 장애 허용 실행 활성화',
          '에이전트 실패 또는 품질 저하 시 자동 에이전트 재배정',
          'BSC에 TEE 증명 파이프라인이 포함된 VerifierGateway 배포(Intel SGX 및 AMD SEV)',
          '증명된 출력물만 보상 대상',
          '개발자 SDK 출시; 생태계 배분에서 지원금 프로그램 활성화',
        ]
      ),
    },
    {
      id: 'phase-5', number: '05', status: 'upcoming',
      phaseLabel: t('Phase 05', '5단계'),
      title: t('Full Decentralization', '완전한 탈중앙화'),
      subtitle: t('2027 and beyond', '2027년 이후'),
      statusLabel: t('Upcoming', '예정'),
      items: t(
        [
          'Full Proof of Swarm consensus; two-thirds validator supermajority to finalize outputs',
          'PLONK-based ZKML proving (up to 7B parameter models, GPU-accelerated)',
          'VRF committee selection via threshold BLS beacon',
          'Recursive proof composition attests to entire swarm execution graphs',
          'Security Council authority progressively reduced toward full DAO governance',
          'Target: dissolution within 24 months of mainnet launch',
        ],
        [
          '완전한 Proof of Swarm 합의 활성화; 모든 작업 출력물 확정에 검증인 3분의 2 절대다수 필요',
          'PLONK 기반 ZKML 증명(최대 7B 파라미터 모델, GPU 가속)',
          '임계값 BLS 비콘을 통한 VRF 위원회 선정 활성화',
          '재귀적 증명 구성으로 전체 스웜 실행 그래프 증명',
          '거버넌스 투표를 통한 보안 위원회 권한 점진적 축소',
          '목표: 메인넷 출시 24개월 이내 해산',
        ]
      ),
    },
  ];

  const statusEntries = [
    { key: 'complete', cfg: statusConfig.complete, label: t('Complete', '완료') },
    { key: 'active',   cfg: statusConfig.active,   label: t('Active', '진행 중') },
    { key: 'upcoming', cfg: statusConfig.upcoming,  label: t('Upcoming', '예정') },
  ];

  return (
    <section id="roadmap" className="py-20 sm:py-24 md:py-32 line-grid"
      style={{ position: 'relative', zIndex: 10, background: 'var(--sb-bg-2)' }}
      data-testid="roadmap-section">
      <div style={{ position: 'absolute', bottom: '15%', left: '-5%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(48,213,200,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 relative">
        <div ref={ref} className={`mb-12 md:mb-16 anim-hidden ${inView ? 'anim-visible' : ''}`}>
          <div className="section-divider" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-3">{t('Roadmap', '로드맵')}</p>
          <h2 className="font-outfit font-black text-white text-3xl sm:text-4xl md:text-5xl tracking-tight">
            {t('Five Phases to', '완전한 탈중앙화를 향한')}<br />
            <span className="gradient-text">{t('Full Decentralization', '5단계')}</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-xl text-xs sm:text-sm md:text-base">
            {t('From on-chain foundation to full Proof of Swarm consensus. A phased path toward decentralized AI coordination.',
               '온체인 기반 구축부터 완전한 Proof of Swarm 합의까지. 탈중앙화 AI 조율을 향한 단계적 여정입니다.')}
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            {statusEntries.map(({ key, cfg, label }) => {
              const Icon = cfg.icon;
              return (
                <div key={key} className="flex items-center gap-2">
                  <Icon size={12} style={{ color: cfg.dot }} />
                  <span className="text-xs font-mono text-white/45">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="max-w-4xl">
          {phases.map((phase, i) => <PhaseCard key={phase.id} phase={phase} index={i} parentInView={inView} />)}
        </div>
      </div>
    </section>
  );
};

export default RoadmapSection;
